import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartCtx = createContext(null);

// Guests get a browser-local cart so they can shop without an account; it is
// merged into the server cart the moment they sign in (via guest checkout OTP).
const GUEST_KEY = "ts_guest_cart";
const readGuest = () => { try { return JSON.parse(localStorage.getItem(GUEST_KEY) || "[]"); } catch { return []; } };
const writeGuest = (arr) => { try { localStorage.setItem(GUEST_KEY, JSON.stringify(arr)); } catch { /* private mode */ } };

export function CartProvider({ children }) {
  const { user } = useAuth();
  const isBuyer = !!user && user.role === "buyer";
  const [items, setItems] = useState([]);

  const loadServer = useCallback(async () => {
    try { const { data } = await api.get("/cart"); setItems(data || []); } catch { setItems([]); }
  }, []);

  // Push any browser-local cart into the server cart, then forget the local copy.
  const mergeGuest = useCallback(async () => {
    const guest = readGuest();
    if (!guest.length) return;
    for (const g of guest) {
      try { await api.post("/cart", { product_id: g.product_id, size: g.size, quantity: g.quantity }); } catch { /* skip a bad line */ }
    }
    writeGuest([]);
  }, []);

  const refresh = useCallback(async () => {
    if (isBuyer) await loadServer();
    else setItems(readGuest());
  }, [isBuyer, loadServer]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isBuyer) { await mergeGuest(); if (alive) await loadServer(); }
      else if (alive) setItems(readGuest());
    })();
    return () => { alive = false; };
  }, [isBuyer, mergeGuest, loadServer]);

  const add = useCallback(async (product_id, size, quantity = 1, product = null) => {
    if (isBuyer) { await api.post("/cart", { product_id, size, quantity }); await loadServer(); return; }
    // Guest: keep enough of the product to render the cart without a fetch.
    let prod = product;
    if (!prod) { try { prod = (await api.get(`/products/${product_id}`)).data; } catch { prod = null; } }
    const guest = readGuest();
    const existing = guest.find((i) => i.product_id === product_id && i.size === size);
    if (existing) existing.quantity = Math.min(20, existing.quantity + quantity);
    else guest.push({ id: `g_${product_id}_${size}_${Date.now()}`, product_id, size, quantity, product: prod });
    writeGuest(guest); setItems([...guest]);
  }, [isBuyer, loadServer]);

  const remove = useCallback(async (item_id) => {
    if (isBuyer) { await api.delete(`/cart/${item_id}`); await loadServer(); return; }
    const guest = readGuest().filter((i) => i.id !== item_id);
    writeGuest(guest); setItems(guest);
  }, [isBuyer, loadServer]);

  const updateQty = useCallback(async (item_id, quantity) => {
    const qty = Math.max(1, Math.min(20, quantity));
    setItems((prev) => prev.map((i) => (i.id === item_id ? { ...i, quantity: qty } : i)));
    if (isBuyer) {
      try { await api.patch(`/cart/${item_id}`, { quantity: qty }); } catch { await loadServer(); }
    } else {
      writeGuest(readGuest().map((i) => (i.id === item_id ? { ...i, quantity: qty } : i)));
    }
  }, [isBuyer, loadServer]);

  const clear = useCallback(async () => {
    if (isBuyer) { await api.delete("/cart"); } else { writeGuest([]); }
    setItems([]);
  }, [isBuyer]);

  const { count, subtotal } = useMemo(() => ({
    count: items.reduce((s, i) => s + i.quantity, 0),
    subtotal: items.reduce((s, i) => s + (i.product?.price || 0) * i.quantity, 0),
  }), [items]);

  const value = useMemo(
    () => ({ items, add, remove, updateQty, clear, refresh, mergeGuest, count, subtotal }),
    [items, add, remove, updateQty, clear, refresh, mergeGuest, count, subtotal]
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
